import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  doSetupBillingAdminUpdateProductUnits,
} from 'store/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { isEqual, toInteger } from 'lodash';
import { useSelector } from 'hooks/useSelector';
import {
  selectBillingProduct,
  selectBillingProductUnits,
} from 'store/remote/selectors';
import { TextField, TextFieldSpecial } from 'components/Field/TextField';
import { CardRow } from 'components/CardRow';
import { smartText } from 'lib/str';
import { formatNumber } from 'lib/pluralise';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { PageHeader } from 'components/PageHeader';
import { GapSmall } from 'components/GapSmall';

export const BillingProductUnitsModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsBillingProductUnits>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const { productId } = route.params;

  const product = useSelector((state) =>
    selectBillingProduct(state, productId),
  );
  const productUnits =
    useSelector((state) => selectBillingProductUnits(state, productId))
      ?.units || 0;

  const dataRef = React.useRef<number>(productUnits);
  const isDirty = React.useCallback(
    () => !isEqual(productUnits, dataRef.current),
    [productUnits],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const handleSave = React.useCallback(() => {
    dispatch(
      doSetupBillingAdminUpdateProductUnits({
        productId,
        units: dataRef.current,
      }),
    );
  }, [dispatch, productId]);

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () =>
          dispatch(
            NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingProductUnits]),
          ),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingProductUnits]));
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = (value: number) => {
    dataRef.current = value;
    forceUpdate();
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  if (!product) {
    return null;
  }

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Product Units"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card>
        <CardRow label={product.name} />
      </Card>
      <GapSmall>
        <Card label="Units">
          <TextField
            textLeft="New No. of Units"
            placeholder="0"
            onChange={(value) => handleChangeValue(toInteger(value || '0'))}
            indentActions
            value={`${dataRef.current}`}
            special={TextFieldSpecial.number}
            onEsc={handleClose}
            autoFocus
            autoFocusDelay
            inputStyle={{ textAlign: 'right' }}
          />
        </Card>
        <Card
          explanation={smartText(
            "You should only add units to the organisation if they're global, like an organisation wide subscription.\n\n" +
              "If you're adding asset specific units (like flats) you should add them directly to the asset.\n\n" +
              'New units will be invoiced at the beginning of next month, no matter what the invoice frequency the product has. ' +
              'If the frequency is annual and next month is the 8th month, the invoice will be for 5 months.',
          )}
        >
          <ViewWithSeparators horizontal>
            <CardRow
              label="Current"
              value={formatNumber(productUnits)}
              style={{ flex: 1 }}
            />
            <CardRow
              label="Change"
              value={
                productUnits > dataRef.current
                  ? formatNumber(dataRef.current - productUnits)
                  : productUnits < dataRef.current
                    ? `+${formatNumber(dataRef.current - productUnits)}`
                    : formatNumber(0)
              }
              style={{ flex: 1 }}
            />
          </ViewWithSeparators>
        </Card>
      </GapSmall>
    </Page>
  );
};
